import { baseApi } from "@/core/api/baseApi";
import type {
  GetMeResponseDTO,
  UserPermissionsResponseDTO,
} from "../../domain/dto/ProfileDTO";
import type { UserProfile } from "../../domain/entities/Profile";
import type { UpdateProfileDTO } from "../../domain/dto/UpdateProfileDTO";
import { mapGetMeResponse } from "./mapper";
import { authSession } from "@/core/api/authSession";
import {
  flattenUserPermissionSlugs,
  mapUserPermissionsResponse,
  type UserPermissionsResult,
} from "./userPermissionsResponse";

export type { UserPermissionsResult };

export const profileApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getMe: builder.query<UserProfile, void>({
      query: () => ({ url: "/users/me" }),
      transformResponse: (response: GetMeResponseDTO) => {
        authSession.setRegionalApi(response.data);
        return mapGetMeResponse(response);
      },
      providesTags: ["Profile"],
    }),

    updateMe: builder.mutation<void, UpdateProfileDTO>({
      query: (body) => ({ url: "/users/me", method: "PATCH", body }),
      invalidatesTags: ["Profile"],
    }),

    getUserPermissions: builder.query<UserPermissionsResult, string>({
      query: (userId) => ({
        url: `/roles-permissions/permissions/${userId}`,
      }),
      transformResponse: (response: UserPermissionsResponseDTO) =>
        mapUserPermissionsResponse(response),
      providesTags: (_result, _error, userId) => [
        { type: "Permissions", id: userId },
      ],
    }),

    getUserPermissionSlugs: builder.query<string[], string>({
      query: (userId) => ({
        url: `/roles-permissions/permissions/${userId}`,
      }),
      transformResponse: (response: UserPermissionsResponseDTO) =>
        flattenUserPermissionSlugs(response),
      providesTags: (_result, _error, userId) => [
        { type: "Permissions", id: userId },
      ],
    }),
  }),
});

export const {
  useGetMeQuery,
  useUpdateMeMutation,
  useGetUserPermissionsQuery,
  useGetUserPermissionSlugsQuery,
} = profileApi;
